import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Ticket, Users, Shield, Calendar } from "lucide-react";

const STATUS_META = {
    active: { color: "text-[#00e676]", label: "Active" },
    accepted: { color: "text-[#a3a3a3]", label: "Already used" },
    revoked: { color: "text-[#ff3333]", label: "Revoked" },
    expired: { color: "text-[#ff3333]", label: "Expired" },
};

const ROLE_LABEL = { team: "Artist Team Member", staff: "Event Staff", manager: "Venue Manager" };
const ROLE_ICON = { team: Users, staff: Shield, manager: Shield };

export default function AcceptInvite() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const { user, loadMyArtists } = useAuth();
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Load preview (public, no auth required)
    useEffect(() => {
        if (!token) { setLoading(false); return; }
        setLoading(true);
        endpoints.authInvitePreview(token)
            .then((d) => setPreview(d))
            .catch((e) => setPreviewError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [token]);

    const accept = async () => {
        setBusy(true);
        setError("");
        try {
            const d = await endpoints.authAcceptInvite(token);
            await loadMyArtists();
            if (d.already_member) {
                toast(d.message || "You're already a member");
            } else {
                toast.success(`Joined as ${d.role === "team" ? "Team Member" : d.role === "manager" ? "Venue Manager" : d.role === "staff" ? "Venue Staff" : "Event Staff"}`);
            }
            nav(d.target_kind === "venue" ? "/venue" : "/workspace", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const goSignIn = () => {
        const next = encodeURIComponent(`/accept-invite?token=${encodeURIComponent(token)}`);
        nav(`/signin?next=${next}`);
    };
    const goSignUp = () => {
        const next = encodeURIComponent(`/accept-invite?token=${encodeURIComponent(token)}`);
        nav(`/signup?next=${next}`);
    };

    if (!token) return <div className="p-10 text-sm text-[#a3a3a3]" data-testid="invite-missing-token">This invite link is missing a token.</div>;
    if (loading || user === null) {
        return (
            <div className="max-w-md mx-auto px-4 md:px-6 py-16" data-testid="invite-loading">
                <div className="border border-[#2a2a2a] p-8 text-sm text-[#a3a3a3] flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading invitation…
                </div>
            </div>
        );
    }
    if (previewError || !preview) {
        return (
            <div className="max-w-md mx-auto px-4 md:px-6 py-16" data-testid="invite-preview-error">
                <div className="border border-[#2a2a2a] p-8">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl">Invite unavailable</h1>
                    <p className="mt-3 text-sm text-[#ff3333] font-mono uppercase">{previewError || "This invite link is not valid."}</p>
                    <Link to="/" className="pm-btn pm-btn-primary w-full mt-6 text-center" data-testid="invite-home-link">Go home</Link>
                </div>
            </div>
        );
    }

    const RoleIcon = ROLE_ICON[preview.role] || Ticket;
    const statusMeta = STATUS_META[preview.status] || STATUS_META.active;
    const canAccept = preview.status === "active";
    const isEventStaff = preview.role === "staff";
    const isVenue = preview.target_kind === "venue";
    const targetName = isVenue ? preview.venue?.name : preview.artist?.name;

    return (
        <div className="max-w-md mx-auto px-4 md:px-6 py-16" data-testid="accept-invite-page">
            <div className="border border-[#2a2a2a] p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint · Invitation</div>
                <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl" data-testid="invite-title">
                    Join {targetName || (isVenue ? "venue" : "artist")}
                </h1>

                <div className="mt-5 border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                    <Row icon={RoleIcon} label="Role" value={ROLE_LABEL[preview.role] || preview.role} testId="invite-role" />
                    <Row icon={Users} label={isVenue ? "Venue" : "Artist"} value={targetName || "—"} testId="invite-target" />
                    {isEventStaff && !isVenue && (
                        <Row icon={Calendar} label="Event" value={preview.event?.name || "—"} testId="invite-event" />
                    )}
                    {preview.inviter_name && (
                        <Row icon={Shield} label="Invited by" value={preview.inviter_name} testId="invite-inviter" />
                    )}
                    <div className="p-3 flex justify-between items-center">
                        <span className="text-[10px] font-mono uppercase text-[#666]">Status</span>
                        <span className={`text-[10px] font-mono uppercase ${statusMeta.color}`} data-testid="invite-status">{statusMeta.label}</span>
                    </div>
                    {preview.expires_at && (
                        <div className="p-3 flex justify-between items-center">
                            <span className="text-[10px] font-mono uppercase text-[#666]">Expires</span>
                            <span className="text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid="invite-expires">{new Date(preview.expires_at).toLocaleString()}</span>
                        </div>
                    )}
                </div>

                {error && <div className="mt-4 text-xs font-mono uppercase text-[#ff3333]" data-testid="invite-error">{error}</div>}

                {!canAccept ? (
                    <div className="mt-6 text-xs font-mono uppercase text-[#a3a3a3]" data-testid="invite-not-active">
                        This invitation is {preview.status}. Ask the artist owner for a new link.
                    </div>
                ) : !user ? (
                    <div className="mt-6 space-y-2">
                        <p className="text-xs font-mono uppercase text-[#a3a3a3]" data-testid="invite-signin-cta">
                            Sign in or create an account to accept.
                        </p>
                        <button onClick={goSignIn} className="pm-btn pm-btn-primary w-full" data-testid="invite-signin-btn">
                            Sign in to accept
                        </button>
                        <button onClick={goSignUp} className="pm-btn w-full border-[#f4f4f0]" data-testid="invite-signup-btn">
                            Create account
                        </button>
                    </div>
                ) : (
                    <button onClick={accept} disabled={busy} className="pm-btn pm-btn-primary w-full mt-6" data-testid="invite-accept-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Accept invitation
                    </button>
                )}
            </div>
        </div>
    );
}

function Row({ icon: Icon, label, value, testId }) {
    return (
        <div className="p-3 flex items-center gap-3">
            <Icon className="w-4 h-4 text-[#ff4400]" />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase text-[#666]">{label}</div>
                <div className="text-sm truncate" data-testid={testId}>{value}</div>
            </div>
        </div>
    );
}
